"use client";

import { cn } from "@health/ui/utils";
import { Loader2 } from "lucide-react";

type AuthButtonProps = {
  type?: "submit" | "button";
  variant: "primary" | "secondary" | "link";
  loading?: boolean;
  disabled?: boolean;
  icon?: React.ReactNode;
  children: React.ReactNode;
  onClick?: () => void;
  className?: string;
  theme?: "light" | "dark";
};

export function AuthButton({
  type = "button",
  variant,
  loading = false,
  disabled = false,
  icon,
  children,
  onClick,
  className,
  theme = "light",
}: AuthButtonProps) {
  const baseClasses = "transition-colors flex items-center justify-center gap-1";

  const getVariantClasses = () => {
    if (variant === "primary") {
      if (theme === "dark") {
        // Modal dark theme
        return "w-full py-4 rounded-full bg-white text-auth-button-text text-base font-semibold hover:bg-white/90 disabled:opacity-40 disabled:cursor-not-allowed";
      }
      // Inline light theme - matches Figma auth modal button
      return cn(
        "w-full pl-1.5 pr-0.5 py-2.5 rounded-[64px] inline-flex flex-col justify-center items-center",
        "bg-(--color-accent) hover:bg-(--color-accent)/90",
        "disabled:opacity-30 disabled:cursor-not-allowed",
      );
    }
    if (variant === "secondary") {
      if (theme === "dark") {
        // Modal dark theme
        return "w-full py-4 rounded-full border border-accent-light/20 text-white/60 text-base font-semibold hover:bg-accent/90 disabled:opacity-40 disabled:cursor-not-allowed";
      }
      // Inline light theme
      return cn(
        "w-full py-5 rounded-full border border-[var(--color-chips-border)]",
        "text-[var(--color-text-primary)]/60 text-base font-normal",
        "hover:bg-black/5 disabled:opacity-40 disabled:cursor-not-allowed",
      );
    }
    // link variant
    return cn(
      theme === "dark"
        ? "text-white/70 hover:text-white"
        : "text-[var(--color-text-primary)]/70 hover:text-[var(--color-text-primary)]",
      "underline font-normal text-xs cursor-pointer disabled:opacity-50",
    );
  };

  const primaryInnerClasses =
    "h-9 px-4 rounded-[60px] inline-flex justify-center items-center gap-1 text-white text-base font-medium leading-[24.85px] tracking-[-0.73px]";

  const content = loading ? (
    <>
      <Loader2
        size={variant === "primary" && theme === "dark" ? 18 : 16}
        className="animate-spin"
      />
      Loading...
    </>
  ) : (
    <>
      {icon}
      {children}
    </>
  );

  if (variant === "primary" && theme === "light") {
    // Inline light theme with nested structure
    return (
      <button
        type={type}
        onClick={onClick}
        disabled={disabled || loading}
        className={cn(baseClasses, getVariantClasses(), className)}
      >
        <div className={primaryInnerClasses}>{content}</div>
      </button>
    );
  }

  // Modal dark theme or secondary/link variants
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      className={cn(baseClasses, getVariantClasses(), className)}
    >
      {content}
    </button>
  );
}
